/**
 * Single source of truth for every color/dash this app uses to draw a
 * network element on either map view (NetworkMap.tsx's plain SVG,
 * GeoreferencedMap.tsx's Leaflet one) and in ElementBrowser.tsx's own
 * swatch legend. UI redesign phase 2 — before this module existed, the same
 * values were duplicated by hand across `useNetworkMapState.ts` (as
 * `NODE_COLOR`/`LINK_COLOR`), literal hex scattered through
 * `GeoreferencedMap.tsx` (22 occurrences), literal hex duplicated a SECOND
 * time in `networkMap.css` (the exact same values, typed out again with
 * nothing to catch a mismatch), and one-off consts in `ElementBrowser.tsx`.
 * Changing a color used to mean finding and editing 2-3 places by hand;
 * now it's exactly one.
 *
 * Design principle (the approved redesign reference,
 * https://claude.ai/artifact/4q75faMmqMukUccFSHWTCd, Tokens.dc.html):
 * "Forma = tipo · color = dato" — a node's SHAPE (circle/triangle/square/
 * diamond, NodesLayer in NetworkMap.tsx/GeoreferencedMap.tsx) already fully
 * identifies its kind on its own, so NODES get one neutral ink color
 * (`NETWORK_INK` below), not a categorical palette — the mockup's own
 * concrete swatch (Tokens.dc.html) shows this literally: all 4 node kinds
 * rendered identically (white fill, the same `#35505f` stroke),
 * distinguished only by shape. LINKS are a different story — see
 * `LINK_INK`'s own header below for why that principle doesn't extend to
 * them (found live, reverted the same day).
 *
 * Theme-invariance is now split, not absolute: `NETWORK_INK`/`LINK_INK`/the
 * selection colors below stay fixed regardless of the app's own light/dark
 * toggle (this IS still real data-identity, same reasoning as
 * `ResultsDashboardCategoryBlock.tsx`'s own `ELEMENT_COLORS`) — but the
 * BASEMAP underneath them now does react to theme (UI redesign phase 6): a
 * CSS filter over GeoreferencedMap.tsx's own OSM tiles in dark mode
 * (`networkMap.css`'s own `.leaflet-tile-pane` rule), not a second tile
 * provider (CARTO's free/keyless endpoint turned out to now require an API
 * key, confirmed live — that module's own header has the full story). The
 * plain SVG canvas (NetworkMap.tsx) stays exactly as light as ever either
 * way — `.network-map-svg`'s own header in networkMap.css explains why
 * ("same as a paper drawing canvas... the real SWMM GUI's own Study Area
 * Map, which has no dark mode either").
 */

/** The single ink used for every node's stroke, any kind — NOT links (see
 * `LINK_INK` below), despite the name predating that split. Matches the
 * approved mockup's own swatch exactly. */
export const NETWORK_INK = "#35505f";

/** A node's own fill (junction/outfall/storage/divider all alike) — white,
 * so the ink stroke above reads as a clean outline, and so a thematic/
 * results-animation override (which replaces this fill, never the stroke)
 * always starts from the same neutral base regardless of kind. */
export const NODE_FILL = "#ffffff";

/** Kept as a per-kind lookup (not collapsed to the bare `NETWORK_INK`
 * constant) purely so existing call sites that index by kind
 * (`NODE_COLOR[node.kind]`) don't all need reshaping for what is, today,
 * the exact same value under every key — see the module header for why
 * every kind shares one ink rather than getting its own hue. */
export const NODE_COLOR: Record<string, string> = {
  junction: NETWORK_INK,
  outfall: NETWORK_INK,
  storage: NETWORK_INK,
  divider: NETWORK_INK,
};

/** A link's own ink — deliberately NOT `NETWORK_INK` (reported live, real
 * projects: a dark neutral line is nearly invisible threaded through a real
 * basemap's own dark-toned roads/parcels, worse still once the dark-theme
 * basemap filter (networkMap.css's own `.leaflet-tile-pane` rule, UI
 * redesign phase 6) makes the whole tile layer dark too — nodes survive
 * that fine on their own white fill, but a THIN LINE has no such margin).
 * A real, saturated blue instead — Okabe-Ito's own, the exact color the
 * approved redesign reference's "Selección: halo, nunca un tono" swatch
 * itself uses for its own "normal" line example (Tokens.dc.html) — reads
 * clearly against typical basemap grays/tans/greens in both themes.
 *
 * Second-audit finding: three "brand blues" coexist and are deliberately
 * distinct — see the note on `--color-primary` in base.css for the full
 * split. `LINK_INK` is specifically a DATA INK on the network map, chosen
 * for colorblind safety alongside `SUBCATCHMENT_COLOR`/`RAINGAGE_COLOR`
 * (all three Okabe-Ito palette members); it is NOT `--color-primary`
 * (`#1d6fa5` / `PRIMARY_INK` in this file, the app's general-purpose
 * interactive blue that re-themes light/dark), and NOT
 * `--color-chrome-accent` (`#22c3d6`, the topbar's identity cyan). Stays
 * fixed regardless of app theme, since a link's identity as "conduit" is
 * data, not chrome. */
export const LINK_INK = "#0072b2";

/** Per-kind lookup, same "existing call sites index by kind" reasoning as
 * NODE_COLOR above — LINK_DASH right below is what actually carries a
 * link's kind. */
export const LINK_COLOR: Record<string, string> = {
  conduit: LINK_INK,
  pump: LINK_INK,
  orifice: LINK_INK,
  weir: LINK_INK,
  outlet: LINK_INK,
};

/**
 * Per-kind dash pattern, layered on top of LINK_COLOR so a link's kind
 * doesn't rely on color alone (WCAG 1.4.1) — `conduit` is absent (solid,
 * the plain default) since it's the vast majority of real links. SVG
 * `stroke-dasharray` syntax, which Leaflet's own `dashArray` path option
 * also accepts verbatim (GeoreferencedMap.tsx), so one map here serves both
 * map implementations and ElementBrowser.tsx's own swatch legend. Now that
 * LINK_COLOR no longer varies by kind, this is the ONLY thing that does.
 */
export const LINK_DASH: Partial<Record<string, string>> = {
  pump: "8 3 2 3",
  orifice: "4 3",
  weir: "10 4",
  outlet: "1 3",
};

/** Subcatchment polygon fill/stroke — a single color, not a per-kind
 * lookup: unlike nodes/links, every subcatchment is the same "kind".
 * Audit finding: the older `#4c9e78` was a close-but-not-strict green
 * next to `LINK_INK`'s Okabe-Ito blue on the same map; homogenized to
 * Okabe-Ito's own bluish green (`#009E73`, palette pair #4) so the two
 * paired colors sit inside the same colorblind-safe palette instead of
 * one being ad hoc. Still distinct in hue from the raingage sky blue
 * below (its own palette pair #3) and every selection/measure color
 * elsewhere in this module. Mirrored verbatim in `networkMap.css`'s own
 * `.network-map-subcatchment` rule (CSS can't import a JS constant). */
export const SUBCATCHMENT_COLOR = "#009e73";

/** Raingage marker fill — same "only one kind" reasoning as
 * SUBCATCHMENT_COLOR above. Audit finding: the older `#3182ce` (Tailwind
 * blue-500) was close enough to `LINK_INK` (Okabe-Ito blue `#0072B2`) to
 * read as two shades of the same blue on a dense basemap; swapped to
 * Okabe-Ito's own sky blue (`#56B4E9`, palette pair #3) so the two blues
 * are semantically related (both "water/weather") without being
 * hue-confusable, and the whole map palette now sits inside one
 * colorblind-safe system. Mirrored verbatim in `networkMap.css`'s own
 * `.network-map-raingage` rule. */
export const RAINGAGE_COLOR = "#56b4e9";

/**
 * Primary selection — reverted live (2026-09-21, real Girona-scale project,
 * reported directly: a selected link was completely indistinguishable from
 * the map). The redesign's own first attempt used a neutral near-black ring
 * (`#16252d`), following the approved mockup's "Selección: halo, nunca un
 * tono" swatch literally — but that swatch's own neutral ring only reads
 * because it wraps an already-COLORED line (its own "normal" example is
 * blue, `LINK_INK` above now matches it) — around this redesign's own
 * neutral-ink default it collapsed into the exact same tone family as the
 * unselected state, and a dark ring on a DARK basemap (the phase 6 tile
 * filter) is doubly self-defeating. Back to the pre-redesign amber instead
 * — proven live before this redesign ever touched it, and the one real
 * reason it left (colliding with a divider node's own amber FILL) no
 * longer applies: every node kind is white-filled now (this module's own
 * header), so there's nothing left for it to collide with. `networkMap.css`
 * still layers a white `filter: drop-shadow(...)` glow on top of this in
 * the SVG map (same mechanism as keyboard `:focus-visible` there) — that
 * part of the redesign holds up fine on its own, only the ring's own COLOR
 * was the problem.
 */
export const SELECTION_RING_OUTER = "#b7791f";

/** Multi-selection (shift-click) — same live finding as
 * SELECTION_RING_OUTER above: the redesign's own neutral gray-blue
 * (`#7d93a0`) read as barely-there against a real basemap, doubly so
 * inverted-dark. Back to the pre-redesign pale yellow, paired with the same
 * dashed stroke so "also selected" still reads by pattern too, not color
 * alone — distinct from the primary ring above by both.
 *
 * Second-audit finding: the `#ecc94b` value below is ALSO
 * `--color-warning`'s DARK-mode value in base.css (the app's general-
 * purpose warning color re-themes light/dark — `#b7791f` mustard in
 * light, `#ecc94b` yellow in dark). Coincidental value overlap, not a
 * mirror of the token: this constant is a selection INK on the map —
 * theme-invariant like every other color in this file (see module
 * header) — while `--color-warning` is a UI STATE color that theme-
 * swaps. Kept as a fixed hex, NOT reached via `var(--color-warning)`,
 * since routing it through the theme swap would flip the map's multi-
 * select highlight to mustard in light mode and break the "yellow
 * regardless of theme" invariant that both map and tree consumers
 * rely on. This constant remains the SINGLE SOURCE OF TRUTH for the
 * multi-select yellow across every consumer: `networkMap.css`'s own
 * `.network-map-*-multiselected` family and `elementBrowser.css`'s
 * `.element-browser-item-multi-selected` both cite it as the value
 * they must match. A future edit that wants a new multi-select tone
 * changes THIS line and updates the two CSS mirrors — `--color-
 * warning` in base.css is a separate concern with its own theme
 * responsibilities. */
export const MULTI_SELECT_COLOR = "#ecc94b";

/** The measure-distance tool's own line/point color — deliberately
 * distinct from every real element's ink/selection colors above, so a
 * measurement is never mistaken for a real network element or a
 * selection. */
export const MEASURE_LINE_COLOR = "#9333ea";

/**
 * App-wide primary blue (mirror of `--color-primary` in base.css, which
 * a CSS rule already reads directly for the SVG map's own
 * `.network-map-drawing-preview`/`.network-map-selection-*` shapes).
 * `GeoreferencedMap.tsx` builds Leaflet `pathOptions` from JS, not CSS,
 * so it needs a JS constant to match. Same value spans an
 * area-drag polygon outline, a hovered link's stroke, the direction
 * arrow at a link's midpoint, and the in-progress subcatchment-draw
 * preview — every one of them is the "interactive UI overlay" story
 * `--color-primary` already tells app-wide.
 */
export const PRIMARY_INK = "#1d6fa5";

/**
 * Vertex-handle fill on the georeferenced map (a plain-square amber
 * dot the user grabs to reshape a link's `[VERTICES]` or a
 * subcatchment's `[POLYGONS]`). Second-audit finding: the previous
 * inline `#f6ad55` had no shared token at all — every other color on
 * this map came from a named constant in this file, this one was the
 * outlier. Mirrored verbatim in `networkMap.css`'s own
 * `.network-map-vertex-handle` fill and `.network-map-midpoint-handle`
 * stroke — a CSS rule can't import a JS value, so both files carry it.
 * Deliberately NOT `PRIMARY_INK`: a vertex handle needs to read
 * against the primary-blue selection stroke of the link/polygon it
 * belongs to, not blend into it.
 */
export const VERTEX_HANDLE_FILL = "#f6ad55";

/**
 * Vertex-handle stroke (mirror of `--color-text` in base.css) — the
 * default outline for a vertex handle, matches `.network-map-vertex-
 * handle`'s own stroke in `networkMap.css`.
 */
export const VERTEX_HANDLE_STROKE = "#1a2530";

/**
 * Keyboard-focused vertex-handle stroke — mirror of `.network-map-
 * vertex-focused` in `networkMap.css`. Deliberately darker than
 * `PRIMARY_INK`: a solid blue ring layered on top of the amber
 * `VERTEX_HANDLE_FILL` needs extra contrast to read as actively
 * focused instead of "another blue link nearby".
 */
export const VERTEX_HANDLE_FOCUS_STROKE = "#2b6cb0";
